import React, { createContext, useState, useEffect } from 'react';
import axios from 'axios';
import { cacheClear } from '../utils/clientCache';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Set up axios interceptor to handle 401 errors (expired/invalid tokens)
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // Auto-logout on 401 (expired or invalid token)
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          delete axios.defaults.headers.common['Authorization'];
          setUser(null);
          // Redirect to admin login if not already there
          if (window.location.pathname !== '/admin') {
            window.location.href = '/admin';
          }
        }
        return Promise.reject(error);
      }
    );

    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');

    // Check for corrupted token strings or non-JWT (must contain dots)
    const isInvalidToken = !token || token === 'undefined' || token === 'null' || !token.includes('.');

    if (token && isInvalidToken) {
      console.warn('Found corrupted/invalid token in localStorage, clearing...', token);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      delete axios.defaults.headers.common['Authorization'];
    } else if (token && userData) {
      try {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        // Refresh user profile from server to pick up is_subadmin, role changes, etc.
        axios.get('/api/auth/me').then(res => {
          const freshUser = res.data;
          setUser(freshUser);
          localStorage.setItem('user', JSON.stringify(freshUser));
        }).catch(() => { /* token expired or invalid - keep cached user until next action */ })
          .finally(() => setLoading(false));
        return; // Don't setLoading(false) below - wait for /me to finish
      } catch (e) {
        // Invalid user data, clear it
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
    setLoading(false);

    // Cleanup interceptor on unmount
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  const login = async (username, password) => {
    try {
      const response = await axios.post('/api/auth/login', { username, password });
      const { token, user } = response.data;

      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(user);

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Login failed'
      };
    }
  };

  // Sign in through Steam: the callback leaves a one-time code on /admin?steam_code=…, swapped here
  // for the same { token, user } as the password login (the JWT never travels in a URL).
  const loginWithSteamCode = async (code) => {
    try {
      const response = await axios.post('/api/auth/steam/exchange', { code });
      const { token, user } = response.data;

      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(user);

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Login failed'
      };
    }
  };

  const logout = () => {
    cacheClear();
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  // A password change ends every login (server raises token_version) and answers with a new token
  // for this browser — store it, or the next request is a 401 and signs this browser out too.
  const replaceToken = (token) => {
    if (!token) return;
    localStorage.setItem('token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  };

  // "Sign out everywhere": every browser of this admin, this one included
  const logoutEverywhere = async () => {
    await axios.post('/api/auth/logout-all');
    logout();
  };

  const clearMustChangePassword = () => {
    setUser(prev => {
      const updated = { ...prev, must_change_password: false };
      localStorage.setItem('user', JSON.stringify(updated));
      return updated;
    });
  };

  return (
    <AuthContext.Provider value={{ user, login, loginWithSteamCode, logout, logoutEverywhere, replaceToken, loading, clearMustChangePassword }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
